/**
 * Attack Surface Graph Component
 * Main workflow visualization using ReactFlow
 * Matches Figma design with proper node positioning and styling
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  ConnectionLineType,
  ReactFlowProvider,
  NodeTypes,
  EdgeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import AgentNode from './AgentNode';
import ToolNode from './ToolNode';
import MCPNode from './MCPNode';
import GenericNode from './GenericNode';
import StartEndNode from './StartEndNode';
import WorkflowEdge from './WorkflowEdge';
import './graphNodes.css';
import { GraphDefinition, GraphNode } from '../../types';

import {
  getNodeType,
  getVulnerabilityCount,
  getGuardrails,
  ensureStartEndNodes,
  applyHierarchicalLayout,
  connectDisconnectedStartEnd,
  assignEdgeHandles,
  getVulnerabilities,
} from '../../utils/graphLayoutUtils';

import { transformVulnerabilityFindings } from '../../utils/transformResults';

interface AttackSurfaceGraphProps {
  graphData: GraphDefinition | null;
  onVulnerabilityClick?: (vulndId: string) => void;
}

const AttackSurfaceGraphInner: React.FC<AttackSurfaceGraphProps> = ({ graphData, onVulnerabilityClick }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const backEdgesRef = useRef<Set<string>>(new Set());

  // Memoize node types to prevent recreation
  const nodeTypes = useMemo<NodeTypes>(() => ({
    agent: AgentNode,
    tool: ToolNode,
    custom_tool: ToolNode,
    mcp_server: MCPNode,
    generic: GenericNode,
    basic: GenericNode,
    default: GenericNode,
    start: StartEndNode,
    end: StartEndNode,
  }), []);

  // Memoize edge types
  const edgeTypes = useMemo<EdgeTypes>(() => ({
    workflow: WorkflowEdge,
  }), []);

  const vulnerabilityFindings = useMemo(
  () => transformVulnerabilityFindings(graphData),
  [graphData]
);

  // Convert graph data to ReactFlow nodes and edges:
  useEffect(() => {
    if (!graphData || !graphData.nodes) return;

    // Convert nodes
    const flowNodes: Node[] = graphData.nodes.map((node) => ({
      id: node.id,
      type: getNodeType(node.node_type, node.name),
      position: { x: 0, y: 0 },
      data: {
        label: node.name,
        description: node.description,
        vulnerabilityCount: getVulnerabilityCount(node),
        isCustom: node.node_type === 'custom_tool',
        isStart: node.node_type === 'start',
        llm: node.node_type === 'agent' ? (node as any).llm : undefined,
        guardrails: getGuardrails(node),
        vulnerabilities: node.node_type === 'tool' || node.node_type === 'custom_tool'
          ? vulnerabilityFindings.filter(v => v.affectedToolId === node.id)
          : [],
        onVulnerabilityClick,
      },
    }));

    // Convert edges - include guardrails data for popover
    const flowEdges: Edge[] = graphData.edges.map((edge, index) => {
      // Get guardrails directly from edge's dk_firewall if present
      const edgeGuardrails: string[] = edge.dk_firewall ? edge.dk_firewall.map(f => f.name) : [];

      // Determine if edge has vulnerability based on dk_firewall presence
      const hasVulnerability = edge.has_vulnerability ?? (edge.dk_firewall && edge.dk_firewall.length > 0);

      const sourceNode = graphData.nodes.find(n => n.id === edge.start_id);
      const targetNode = graphData.nodes.find(n => n.id === edge.end_id);
      
      
      // Combine guardrails from both nodes for this edge
      const guardrails: string[] = [];
      if (sourceNode) {
        getGuardrails(sourceNode).forEach(g => {
          if (!guardrails.includes(g)) guardrails.push(g);
        });
      }
      if (targetNode) {
        getGuardrails(targetNode).forEach(g => {
          if (!guardrails.includes(g)) guardrails.push(g);
        });
      }

      return {
        id: `edge-${index}`,
        source: edge.start_id,
        target: edge.end_id,
        type: 'workflow',
        data: {
          hasVulnerability: hasVulnerability,
          vulnerabilityCount: edge.dk_firewall?.length ?? 0,
          guardrails: edgeGuardrails.length > 0 ? edgeGuardrails :[],
          edgeIndex: index,
        },
      };
    });

    // Ensure START and END nodes exist
    const { nodes: nodesWithStartEnd, edges: edgesWithStartEnd } = ensureStartEndNodes(flowNodes, flowEdges);

    // Connect START and END nodes if they're no connected to the graph

    const connectedEdges = connectDisconnectedStartEnd(nodesWithStartEnd, edgesWithStartEnd);

    // Apply hierarchical layout - destructure both positionedNodes and backEdges
    const { positionedNodes, backEdges } = applyHierarchicalLayout(nodesWithStartEnd, connectedEdges, graphData);
    
    backEdgesRef.current = backEdges;

    // Assign optimal handles to edges based on node positions
    const edgesWithHandles = assignEdgeHandles(positionedNodes, connectedEdges, backEdges);

    setNodes(positionedNodes);
    setEdges(edgesWithHandles);
  }, [graphData, setNodes, setEdges]);

  // Render edge handles (node exists/entries dynamically)
  useEffect(() => {
    if (!nodes.length || !edges.length) return;
    // Only update handles if nodes have changed (e.g., after drag)
    setEdges(assignEdgeHandles(nodes, edges, backEdgesRef.current));
  }, [nodes]);

  if (!graphData) {
    return (
      <div className="workflow-graph-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#808080' }}>Error: No workflow data available</p>
      </div>
    );
  }

  return (
    <div 
      className="workflow-graph-container"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        style={{ width: '100%', height: '100%' }}
        defaultEdgeOptions={{type: 'workflow'}}
        defaultMarkerColor="#49495B"
      >
        <Background color="#1e1e2e" gap={72} size={1} />
        <Controls 
          style={{ 
            background: '#212029', 
            borderRadius: '8px',
          }}
          showZoom={true}
          showFitView={true}
          showInteractive={false}
        />
      </ReactFlow>
    </div>
  );
};

// Wrap with ReactFlowProvider for proper context
const AttackSurfaceGraph: React.FC<AttackSurfaceGraphProps> = (props) => {
  return (
    <ReactFlowProvider>
      <AttackSurfaceGraphInner {...props} />
    </ReactFlowProvider>
  );
};

export default AttackSurfaceGraph;
